// File: src/products/equityManager/EquityManagerApp.js
import React, { useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { Building, Users, Briefcase, FileText, Home, Upload, BarChart3, Bell, ArrowLeftRight, Calculator, TrendingUp, Activity } from 'lucide-react';

// Import Stores
import { useGlobalStore } from '../../state/globalStore';
import { useEquityManagerStore } from './state/equityManagerStore';

// Import Components
import Sidebar from '../../components/layout/Sidebar';
import SharedHeader from '../../components/layout/SharedHeader';

// Import Pages
import EquityHomePage from './pages/EquityHomePage';
import CompaniesPage from './pages/CompaniesPage';
import ShareholdersPage from './pages/ShareholdersPage';
import IssuancesPage from './pages/IssuancesPage';
import TransactionsPage from './pages/TransactionsPage';
import ReportsPage from './pages/ReportsPage';
import ImportPage from './pages/ImportPage';
import NotificationsPage from './pages/NotificationsPage';
import DirectorsPage from './pages/DirectorsPage';
import WhatIfScenarioPage from './pages/WhatIfScenarioPage';
import SummaryByRoundPage from './pages/SummaryByRoundPage';
import ActivityLogsPage from './pages/ActivityLogsPage';

const EquityManagerApp = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Global State
    const { user, userProfile, loading: userLoading, initUser } = useGlobalStore();

    // Module State
    const {
        companies, selectedCompany, shareholders, directors, shareClasses, issuances,
        loading: equityLoading, loadingCompanyData, messages, userRole, canCreate,
        addMessage, addSuccess, addError, checkCompanyRole, loadInitialData, loadCompanyData,
        handleCompanyChange, handleCompanyCreated, refreshData, setSelectedCompany
    } = useEquityManagerStore();

    const loading = userLoading || equityLoading;

    // Initial data load
    useEffect(() => {
        initUser();
    }, [initUser]);

    useEffect(() => {
        if (user?.id) {
            loadInitialData();
        }
    }, [user?.id, loadInitialData]);

    // When company changes, load its data and check permissions
    useEffect(() => {
        if (selectedCompany?.id) {
            checkCompanyRole(selectedCompany.id);
            loadCompanyData();
        }
    }, [selectedCompany, checkCompanyRole, loadCompanyData]);

    const navItems = [
        { id: '/equity', icon: Home, label: 'Home' },
        { id: '/equity/companies', icon: Building, label: 'Companies' },
        { id: '/equity/shareholders', icon: Users, label: 'Shareholders' },
        { id: '/equity/directors', icon: Briefcase, label: 'Directors' },
        { id: '/equity/shares', icon: FileText, label: 'Shares' },
        { id: '/equity/transact', icon: ArrowLeftRight, label: 'Transactions' },
        { id: '/equity/scenario-model', icon: Calculator, label: 'Scenario Model' },
        { id: '/equity/summary-by-round', icon: TrendingUp, label: 'Summary by Round' },
        { id: '/equity/reports', icon: BarChart3, label: 'Reports' },
        { id: '/equity/import', icon: Upload, label: 'Import' },
        { id: '/equity/notify', icon: Bell, label: 'Notify' },
        { id: '/equity/activity-logs', icon: Activity, label: 'Activity Logs' }
    ];

    const getActivePage = () => {
        const path = location.pathname;
        if (path === '/equity' || path === '/equity/') return 'home';
        if (path.includes('/companies')) return 'companies';
        if (path.includes('/shareholders')) return 'shareholders';
        if (path.includes('/directors')) return 'directors';
        if (path.includes('/shares')) return 'shares';
        if (path.includes('/transact')) return 'transact';
        if (path.includes('/scenario-model')) return 'scenario-model';
        if (path.includes('/summary-by-round')) return 'summary-by-round';
        if (path.includes('/reports')) return 'reports';
        if (path.includes('/import')) return 'import';
        if (path.includes('/notify')) return 'notify';
        if (path.includes('/activity-logs')) return 'activity-logs';
        return 'home';
    };

    const activePage = getActivePage();

    const renderPageContent = () => {
        if (!selectedCompany && activePage !== 'companies') {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Building className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
                        <p className="text-gray-600 mb-4">Please select or create a company to continue</p>
                        <button
                            onClick={() => navigate('/equity/companies')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Go to Companies
                        </button>
                    </div>
                </div>
            );
        }

        const commonProps = {
            selectedCompany,
            user,
            userRole,
            canCreate,
            addMessage,
            addSuccess,
            addError,
            refreshData,
            onDataRefresh: refreshData
        };

        const loadingSpinner = (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );

        if (loadingCompanyData && activePage !== 'companies') {
            return loadingSpinner;
        }

        return (
            <Routes>
                <Route path="/" element={<EquityHomePage {...commonProps} companyData={selectedCompany} shareClasses={shareClasses} shareholders={shareholders} issuances={issuances} userProfile={userProfile} />} />
                <Route path="/companies" element={<CompaniesPage {...commonProps} companies={companies} setSelectedCompany={setSelectedCompany} onCompanyChange={handleCompanyChange} onCompanyCreated={handleCompanyCreated} />} />
                <Route path="/shareholders" element={<ShareholdersPage {...commonProps} shareholders={shareholders} />} />
                <Route path="/directors" element={<DirectorsPage {...commonProps} directors={directors} />} />
                <Route path="/shares" element={<IssuancesPage {...commonProps} issuances={issuances} shareClasses={shareClasses} shareholders={shareholders} />} />
                <Route path="/transact" element={<TransactionsPage {...commonProps} />} />
                <Route path="/scenario-model" element={<WhatIfScenarioPage {...commonProps} shareholders={shareholders} shareClasses={shareClasses} issuances={issuances} />} />
                <Route path="/summary-by-round" element={<SummaryByRoundPage {...commonProps} selectedCompany={selectedCompany} companyData={selectedCompany} shareholders={shareholders} shareClasses={shareClasses} issuances={issuances} />} />
                <Route path="/reports" element={<ReportsPage {...commonProps} companyData={selectedCompany} shareClasses={shareClasses} shareholders={shareholders} issuances={issuances} />} />
                <Route path="/import" element={<ImportPage {...commonProps} />} />
                <Route path="/notify" element={<NotificationsPage {...commonProps} directors={directors} shareholders={shareholders} />} />
                <Route path="/activity-logs" element={<ActivityLogsPage {...commonProps} selectedCompany={selectedCompany} companies={companies} />} />
            </Routes>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar
                items={navItems.map(item => ({ id: item.id, icon: item.icon, label: item.label }))}
                activeItem={location.pathname}
                onItemClick={(itemId) => navigate(itemId)}
                productName="Equity"
                user={user}
                userProfile={userProfile}
                onBackToProducts={() => navigate('/my-account/dashboard')}
            />
            <div className="flex-1 flex flex-col overflow-hidden">
                <SharedHeader
                    user={user}
                    userProfile={userProfile}
                    userRole={userRole}
                    productName="Equity"
                    selectedCompany={selectedCompany}
                    companies={companies}
                    setSelectedCompany={handleCompanyChange} // Use the action from the store
                    onShowSubscriptions={() => navigate('/my-account/subscriptions')}
                />
                {messages.length > 0 && (
                    <div className="fixed top-20 right-4 z-50 space-y-2" style={{ maxWidth: '400px' }}>
                        {messages.map(message => (
                            <div
                                key={message.id}
                                className={`p-4 rounded-lg shadow-lg ${
                                    message.type === 'error' 
                                        ? 'bg-red-50 border-l-4 border-red-400 text-red-700' 
                                        : 'bg-green-50 border-l-4 border-green-400 text-green-700'
                                }`}
                            >
                                <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium">{message.content}</p>
                                    <button 
                                        onClick={() => addMessage(message.id, 'remove')} // A bit of a hack to remove, should be a dedicated action
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
                    {renderPageContent()}
                </main>
            </div>
        </div>
    );
};

export default EquityManagerApp;
